import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ConfigService } from './config.service';
import { core, getUnitInfo, unitFormatter } from '@icc/shared/helpers/b2b';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
    providedIn: 'root',
})
export class PricesDataService {
    productPrices: Record<number, Record<number, any>> = {};
    configuratorPrices: Record<number, any[]> = {};
    contractorPrices: Record<number, Record<number, Record<number, any>>> = {};
    productUnits: Record<number, any> = {};

    loadedPrices$: Record<number, Promise<void>> = {};
    loadedPrices: Record<number, boolean> = {};
    private resolveLoadedPrices: Record<number, () => void> = {};

    loadedContractorPrices$: Record<number, Record<number, Promise<void>>> = {};
    loadedContractorPrices: Record<number, Record<number, boolean>> = {};
    private resolveLoadedContractorPrices: Record<number, Record<number, () => void>> = {};

    priceRasters: {
        [priceVersionId: number]: {
            [configuratorId: string]: any[];
        }
    } = {};
    loadedPriceRasters$: {
        [priceVersionId: number]: {
            [configuratorId: string]: Promise<void>;
        }
    } = {};
    loadedPriceRasters: {
        [priceVersionId: number]: {
            [configuratorId: string]: boolean;
        }
    } = {};
    private resolveLoadedPriceRasters: {
        [priceVersionId: number]: {
            [configuratorId: string]: () => void;
        }
    } = {};

    loaded$ = new BehaviorSubject<{
        [priceVersionId: number]: {
            [configuratorId: string]: boolean;
        }
    }>({});
    loaded: {
        [priceVersionId: number]: {
            [configuratorId: string]: boolean;
        }
    } = {};
    loading: Record<string, boolean> = {};

    constructor(private http: HttpClient, private configService: ConfigService, private translateService: TranslateService) {}

    public async loadPricesForPriceVersion(id: number) {
        this.loading[`prices-${id}`] = true;
        if (!this.loadedPrices$[id]) {
            this.loadedPrices$[id] = new Promise((resolve) => {
                this.resolveLoadedPrices[id] = resolve;
            });
        }
        if (!this.productPrices[id] || !this.configuratorPrices[id]) {
            const result = await this.http
                .get<any>(
                    `${window.location.origin}/api/b2b/prices/${id}?lang=${this.configService.config.languageCode}&marketId=${this.configService.user?.user?.ActiveMarket}`
                )
                .toPromise();
            this.productPrices[id] = result?.productPrices || {};
            this.configuratorPrices[id] = result?.configuratorPrices || [];
            this.productUnits = result?.productUnits || {};
            this.updateUnitsTranslations();

            this.loadedPrices[id] = true;
            this.resolveLoadedPrices[id]();
        }
        this.loading[`prices-${id}`] = false;
    }

    public async loadPricesForContractor(contractorCompanyId: number, priceVersionId: number) {
        this.loading[`prices-contractor-${priceVersionId}-${contractorCompanyId}`] = true;
        if (!this.loadedContractorPrices$[priceVersionId]) {
            this.loadedContractorPrices$[priceVersionId] = {};
        }
        if (!this.loadedContractorPrices$[priceVersionId][contractorCompanyId]) {
            this.loadedContractorPrices$[priceVersionId][contractorCompanyId] = new Promise((resolve) => {
                if (!this.resolveLoadedContractorPrices[priceVersionId]) {
                    this.resolveLoadedContractorPrices[priceVersionId] = {};
                }
                this.resolveLoadedContractorPrices[priceVersionId][contractorCompanyId] = resolve;
            });
        }
        if (!this.contractorPrices[priceVersionId]?.[contractorCompanyId]) {
            const result = await this.http
                .get<any>(
                    `${window.location.origin}/api/b2b/prices-contractor/${contractorCompanyId}/${priceVersionId}`
                )
                .toPromise();
            if (!this.contractorPrices[priceVersionId]) {
                this.contractorPrices[priceVersionId] = {};
            }
            this.contractorPrices[priceVersionId][contractorCompanyId] = result?.contractorPrices || {};

            if (!this.loadedContractorPrices[priceVersionId]) {
                this.loadedContractorPrices[priceVersionId] = {};
            }
            this.loadedContractorPrices[priceVersionId][contractorCompanyId] = true;
            this.resolveLoadedContractorPrices[priceVersionId][contractorCompanyId]();
        }
        this.loading[`prices-contractor-${priceVersionId}-${contractorCompanyId}`] = false;
    }

    public async loadPriceRastersForPriceVersion(id: number, configuratorId: string) {
        this.loading[`rasters-${id}-${configuratorId}`] = true;
        if (!this.loadedPriceRasters$[id]?.[configuratorId]) {
            if (!this.loadedPriceRasters$[id]) {
                this.loadedPriceRasters$[id] = {};
            }
            this.loadedPriceRasters$[id][configuratorId] = new Promise((resolve) => {
                if (!this.resolveLoadedPriceRasters[id]) {
                    this.resolveLoadedPriceRasters[id] = {};
                }
                this.resolveLoadedPriceRasters[id][configuratorId] = resolve;
            });
        }
        if (!this.priceRasters[id]?.[configuratorId]) {
            const result = await this.downloadPriceRastersStream(id, configuratorId);
            if (!this.priceRasters[id]) {
                this.priceRasters[id] = {};
            }
            this.priceRasters[id][configuratorId] = [];
            result.forEach((priceRaster) => {
                priceRaster.configurators?.forEach((configurator: string) => {
                    if (!this.priceRasters[id][configurator]) {
                        this.priceRasters[id][configurator] = [];
                    }
                    this.priceRasters[id][configurator].push(
                        priceRaster
                    );
                });
            });
            if (!this.loadedPriceRasters[id]) {
                this.loadedPriceRasters[id] = {};
            }
            this.loadedPriceRasters[id][configuratorId] = true;
            this.resolveLoadedPriceRasters[id][configuratorId]();
        }
        this.loading[`rasters-${id}-${configuratorId}`] = false;
    }

    async getProductPricesForPriceVersion(priceVersion: number, contractorCompanyId?: number | null) {
        if (!this.loading[`prices-${priceVersion}`]) {
            this.loadPricesForPriceVersion(priceVersion);
        }
        await this.loadedPrices$[priceVersion];
        if (contractorCompanyId) {
            this.loadPricesForContractor(contractorCompanyId, priceVersion);
            await this.loadedContractorPrices$[priceVersion][contractorCompanyId];
            const productPrices = core.copy(this.productPrices[priceVersion]);
            for (const productId in productPrices) {
                if (this.contractorPrices[priceVersion]?.[contractorCompanyId]?.[productPrices[productId].id]) {
                    productPrices[productId].value = this.contractorPrices[priceVersion][contractorCompanyId][productPrices[productId].id];
                }
                if (productPrices[productId].priceUnits?.length) {
                    for (const priceUnit of productPrices[productId].priceUnits) {
                        if (this.contractorPrices[priceVersion]?.[contractorCompanyId]?.[priceUnit.priceId]) {
                            priceUnit.value = this.contractorPrices[priceVersion][contractorCompanyId][priceUnit.priceId];
                        }
                    }
                }
            }
            return productPrices;

        }
        return this.productPrices[priceVersion];
    }

    async getConfiguratorPricesForPriceVersion(priceVersion: number, contractorCompanyId?: number | null) {
        if (!this.loading[`prices-${priceVersion}`]) {
            this.loadPricesForPriceVersion(priceVersion);
        }
        await this.loadedPrices$[priceVersion];
        if (contractorCompanyId) {
            this.loadPricesForContractor(contractorCompanyId, priceVersion);
            await this.loadedContractorPrices$[priceVersion][contractorCompanyId];
        }
        return this.getConfiguratorPricesForContractor(priceVersion, contractorCompanyId);
    }

    async getPriceRastersForPriceVersion(priceVersion: number, configuratorId: string) {
        if (!this.loading[`rasters-${priceVersion}-${configuratorId}`]) {
            this.loadPriceRastersForPriceVersion(priceVersion, configuratorId);
        }
        await this.loadedPriceRasters$[priceVersion][configuratorId];
        return this.priceRasters[priceVersion];
    }

    getConfiguratorPricesForPriceVersionSync(priceVersion: number, contractorCompanyId?: number | null) {
        return this.getConfiguratorPricesForContractor(priceVersion, contractorCompanyId);
    }

    getPriceRastersForPriceVersionSync(priceVersion: number) {
        return this.priceRasters[priceVersion];
    }


    async loadPricesForConfiguratorAndPriceVersion(priceVersion: number, configuratorId: string) {
        if (!this.loading[`prices-${priceVersion}`]) {
            this.loadPricesForPriceVersion(priceVersion);
        }
        if (!this.loading[`rasters-${priceVersion}-${configuratorId}`]) {
            this.loadPriceRastersForPriceVersion(priceVersion, configuratorId);
        }
        await this.loadedPrices$[priceVersion];
        await this.loadedPriceRasters$[priceVersion][configuratorId];
        if (!this.loaded[priceVersion]) {
            this.loaded[priceVersion] = {};
        }
        this.loaded[priceVersion][configuratorId] = this.loadedPriceRasters[priceVersion][configuratorId] && this.loadedPrices[priceVersion];
        this.loaded$.next(this.loaded);
    }

    getConfiguratorPricesForContractor(priceVersion: number, contractorCompanyId?: number | null) {
        if (!contractorCompanyId || !this.contractorPrices[priceVersion]?.[contractorCompanyId]) {
            return this.configuratorPrices[priceVersion];
        }
        const configuratorPrices = core.copy(this.configuratorPrices[priceVersion]);
        for (const price of configuratorPrices) {
            if (this.contractorPrices[priceVersion]?.[contractorCompanyId][price.id]) {
                price.value = this.contractorPrices[priceVersion][contractorCompanyId][price.id];
            }

            if (price.priceUnits?.length) {
                for (const priceUnit of price.priceUnits) {
                    if (this.contractorPrices[priceVersion]?.[contractorCompanyId]?.[priceUnit.priceId]) {
                        priceUnit.value = this.contractorPrices[priceVersion][contractorCompanyId][priceUnit.priceId];
                    }
                }
            }
        }
        return configuratorPrices;
    }

    /**
     * Downloads price rasters stream containing ND-JSON data using fetch and Stream API
     */
    async downloadPriceRastersStream(priceVersionId: number, configuratorId: string) {
        const response = await fetch(`${window.location.origin}/api/b2b/price-rasters/${priceVersionId}/${configuratorId}`);
        const reader = response.body
            .pipeThrough(new TextDecoderStream())
            // split the stream into lines
            .pipeThrough((() => {
                let buffer = '';
                return new TransformStream({
                    transform(chunk, controller) {
                        buffer += chunk;
                        const parts = buffer.split('\n');
                        parts.slice(0, -1).forEach(part => controller.enqueue(part));
                        buffer = parts[parts.length - 1];
                    },
                    flush(controller) {
                        if (buffer) controller.enqueue(buffer);
                    }
                });
            })())
            .pipeThrough(new TransformStream({
                transform(chunk, controller) {
                    controller.enqueue(JSON.parse(chunk));
                }
            }))
            .getReader();

        const result = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            result.push(value);
        }

        return result;
    }

    getProductUnit(unitId: number) {
        return this.productUnits[unitId];
    }

    getProductUnits() {
        return this.productUnits;
    }

    getUnitInfo(unitId: number, unitCode: string) {
        if (unitId) {
            return this.productUnits[unitId];
        }
        return getUnitInfo(unitCode);
    }

    updateUnitsTranslations() {
        const unitsTranslations: any = {};
        for (const unitId in this.productUnits) {
            const unit = this.productUnits[unitId];
            unitsTranslations[unit.name] = unit.name;
            unitsTranslations[unit.symbol] = unit.symbol;
            unitsTranslations[unit.formattedName] = unit.formattedName;
        }
        this.translateService.setTranslation(this.configService.config.languageCode, unitsTranslations, true);
    }
}
